import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/** A paginated grid with retryable requests and stale-response guards. */
export default class PaginatedList extends Component {
  pages = new Map();
  infinityScrollObserver;
  #rendering = new Set();
  #generation = 0;
  #filterPending = false;
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();
    this.#filterPending = false;
    this.#initializePageRange();
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    this.#observeViewMore();
    this.#prefetch('next');
    this.#prefetch('previous');
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.addEventListener('wm:section-rendered', this.#handleSectionRendered);
    document.addEventListener('wm:section-render-error', this.#handleSectionError);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    this.pages.clear();
    this.infinityScrollObserver?.disconnect();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.removeEventListener('wm:section-rendered', this.#handleSectionRendered);
    document.removeEventListener('wm:section-render-error', this.#handleSectionError);
  }

  #sentinel(type) {
    return this.refs[type === 'previous' ? 'viewMorePrevious' : 'viewMoreNext'];
  }

  #observeViewMore() {
    this.infinityScrollObserver?.disconnect();
    const sentinels = [this.#sentinel('previous'), this.#sentinel('next')].filter(Boolean);
    if (!sentinels.length) return;
    this.infinityScrollObserver = new IntersectionObserver(async (entries) => {
      if (viewTransition.current) await viewTransition.current;
      if (!this.isConnected || this.#filterPending) return;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (entry.target === this.#sentinel('previous')) void this.#renderPage('previous');
        if (entry.target === this.#sentinel('next')) void this.#renderPage('next');
      }
    }, { rootMargin: '100px' });
    sentinels.forEach((sentinel) => this.infinityScrollObserver.observe(sentinel));
  }

  #initializePageRange(reset = false) {
    const { grid } = this.refs;
    if (!grid) return;
    if (!reset && grid.dataset.wmFirstLoadedPage && grid.dataset.wmLastLoadedPage) return;
    const pages = [...grid.children]
      .filter((card) => card.matches('[ref="cards[]"]') && !card.hasAttribute('data-wm-catalog-featured'))
      .map((card) => Number(card.dataset.page)).filter((page) => Number.isInteger(page) && page > 0);
    const currentPage = Number(new URL(window.location.href).searchParams.get('page')) || 1;
    grid.dataset.wmFirstLoadedPage = String(pages.length ? Math.min(...pages) : currentPage);
    grid.dataset.wmLastLoadedPage = String(pages.length ? Math.max(...pages) : currentPage);
  }

  #getPage(type) {
    const { grid } = this.refs;
    if (!grid) return;
    this.#initializePageRange();
    // Keep the loaded range even if an ad's duplicate is the only card on the
    // final page and deduplication removes it. Never fetch that page forever.
    const boundary = Number(type === 'previous' ? grid.dataset.wmFirstLoadedPage : grid.dataset.wmLastLoadedPage);
    const page = boundary + (type === 'previous' ? -1 : 1);
    if (!Number.isInteger(page) || page < 1 || page > Number(grid.dataset.lastPage)) return;
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return { page, url };
  }

  async #fetchPage(info, generation) {
    if (this.pages.has(info.page)) return this.pages.get(info.page);
    const html = await sectionRenderer.getSectionHTML(this.sectionId, true, info.url);
    if (generation === this.#generation && this.isConnected) this.pages.set(info.page, html);
    return html;
  }

  #prefetch(type) {
    if (!this.isConnected || this.#filterPending || !this.#sentinel(type)) return;
    const info = this.#getPage(type);
    if (info) void this.#fetchPage(info, this.#generation).catch(() => {});
  }

  async #renderPage(type) {
    if (!this.isConnected || this.#filterPending) return;
    const generation = this.#generation;
    const key = generation + ':' + type;
    if (this.#rendering.has(key)) return;
    const info = this.#getPage(type);
    const { grid } = this.refs;
    if (!info || !grid) return;
    this.#rendering.add(key);
    this.#sentinel(type)?.querySelector('[data-wm-pagination-retry]')?.remove();
    try {
      const html = await this.#fetchPage(info, generation);
      if (generation !== this.#generation || !this.isConnected || grid !== this.refs.grid) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = parsed.querySelector('[ref="grid"]');
      if (!incoming) throw new Error('Product grid response is incomplete');
      const cards = [...incoming.children].filter((card) => card.matches('[ref="cards[]"]'));
      if (!cards.length) throw new Error('Product grid response has no cards');
      const first = grid.firstElementChild;
      const before = first?.getBoundingClientRect().top || 0;
      if (type === 'previous') grid.prepend(...cards);
      else grid.append(...cards);
      grid.dataset.wmFirstLoadedPage = String(Math.min(Number(grid.dataset.wmFirstLoadedPage), info.page));
      grid.dataset.wmLastLoadedPage = String(Math.max(Number(grid.dataset.wmLastLoadedPage), info.page));
      this.#aspectRatioHelper?.processNewElements();
      history.pushState('', '', info.url.toString());
      if (type === 'previous' && first) window.scrollBy(0, first.getBoundingClientRect().top - before);
      requestIdleCallback(() => this.#prefetch(type));
    } catch {
      if (generation === this.#generation && this.isConnected) {
        this.pages.delete(info.page);
        this.#showRetry(type, 'Load more products', () => this.#renderPage(type));
      }
    } finally {
      this.#rendering.delete(key);
    }
  }

  #showRetry(type, text, retry) {
    const sentinel = this.#sentinel(type) || this;
    if (sentinel.querySelector('[data-wm-pagination-retry]')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-secondary';
    button.dataset.wmPaginationRetry = '';
    button.textContent = text;
    button.addEventListener('click', () => { button.remove(); void retry(); }, { once: true });
    sentinel.append(button);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #handleFilterUpdate = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    this.#generation++;
    this.pages.clear();
    this.#filterPending = true;
    this.infinityScrollObserver?.disconnect();
  };

  #handleSectionRendered = (event) => {
    if (event.detail?.sectionId !== this.sectionId || event.detail.mode === 'hydration') return;
    this.#generation++;
    this.pages.clear();
    this.#filterPending = false;
    this.querySelectorAll('[data-wm-pagination-retry]').forEach((button) => button.remove());
    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.#initializePageRange(true);
      this.#observeViewMore();
      this.#prefetch('next');
      this.#prefetch('previous');
    });
  };

  #handleSectionError = (event) => {
    if (event.detail?.sectionId !== this.sectionId || !this.#filterPending) return;
    this.#showRetry('next', 'Retry loading products', () =>
      sectionRenderer.renderSection(this.sectionId).catch(() => {}));
  };
}
